// ─── QUICK MATCH (5 questions) ────────────────────────────────────────────────

export const QUICK_QUESTIONS = [
  {
    type: 'lifestyle', backendId: 1,
    section: 'Attachment',
    question: "When someone I'm dating pulls back or goes quiet, my instinct is:",
    note: "There's no right answer — we're looking for your honest pattern.",
    options: [
      { text: "I reach out and need reassurance — distance makes me anxious", value: 'anxious' },
      { text: "I give them space and trust the connection", value: 'secure' },
      { text: "I pull back too — I prefer not to seem too available", value: 'avoidant' },
      { text: "I feel confused — part of me reaches out, part wants to disappear", value: 'disorganized' },
    ],
  },
  {
    type: 'lifestyle', backendId: 22,
    section: 'Intention',
    question: "What I'm genuinely looking for right now:",
    note: "Be honest — matching you with someone at the same life stage matters more than sounding a certain way.",
    options: [
      { text: "Long term relationship leading to marriage", value: 'marriage' },
      { text: "Long term committed relationship", value: 'long_term' },
      { text: "Short term for now, open to long term", value: 'short_term_to_ltr' },
      { text: "Short term or casual", value: 'short_term' },
      { text: "Open to whatever — the connection matters more than the label", value: 'open' },
    ],
  },
  {
    type: 'lifestyle', backendId: 14,
    section: 'Life Vision',
    question: "When it comes to children:",
    options: [
      { text: "I want children — it's a clear part of my future", value: 'want' },
      { text: "I'm open if the situation is right", value: 'open' },
      { text: "Genuinely unsure — I'd decide with a partner", value: 'unsure' },
      { text: "I don't want children — this is settled for me", value: 'no' },
    ],
  },
  {
    type: 'lifestyle', backendId: 5,
    section: 'Attachment',
    question: "How intertwined I want my life to be with a partner:",
    options: [
      { text: "Deeply merged — I want us to be each other's primary world", value: 'merged' },
      { text: "Closely connected but we both maintain our own lives", value: 'connected' },
      { text: "Parallel lives with strong connection", value: 'parallel' },
      { text: "Flexible — I'll figure it out with the right person", value: 'flexible' },
    ],
  },
  {
    type: 'intimacy', backendId: 'intimacy_gateway',
    section: 'Intimacy',
    question: "When it comes to physical intimacy with a partner:",
    options: [
      { text: "I'm fairly private about this — I know what I like", value: 'traditional' },
      { text: "I'm open to sharing more — compatibility here matters to me", value: 'alternative' },
    ],
  },
];

// ─── DEEP SEARCH — EXTRA QUESTIONS (added after QuickMatch, no duplicates) ────
// QuickMatch covers: backendId 1, 5, 14, 22 (lifestyle) and intimacy_gateway
// These 38 questions pick up from there.

export const DEEP_QUESTIONS = [
  // ── ATTACHMENT (3 after removing backendId 1 and 5, both in QuickMatch) ──────
  {
    type: 'lifestyle', backendId: 2,
    section: 'Attachment',
    question: "Deep emotional intimacy — being truly known by someone — feels:",
    options: [
      { text: "Like what I most want and also what scares me most — I crave it but fear being too much or not enough", value: 'anxious' },
      { text: "Natural and nourishing — vulnerability feels safe when there's trust", value: 'secure' },
      { text: "Uncomfortable — I value independence and closeness can feel like losing myself", value: 'avoidant' },
      { text: "Confusing — I want it but don't fully trust it when I have it", value: 'disorganized' },
    ],
  },
  {
    type: 'lifestyle', backendId: 3,
    section: 'Attachment',
    question: "Looking back at past relationships, my most consistent pattern has been:",
    options: [
      { text: "Giving a lot, worrying about the relationship's security, feeling like I care more than they do", value: 'anxious' },
      { text: "Mostly stable — relationships end for real reasons, not recurring cycles", value: 'secure' },
      { text: "Feeling suffocated — needing more space than partners wanted to give", value: 'avoidant' },
      { text: "A mix of intense connection followed by pushing people away or attracting partners who do", value: 'disorganized' },
    ],
  },
  {
    type: 'lifestyle', backendId: 4,
    section: 'Attachment',
    question: "When I imagine a healthy long-term relationship, what I most want is:",
    options: [
      { text: "Deep emotional security — to know without doubt that my partner is fully committed", value: 'anxious' },
      { text: "A secure base — someone I can return to fully while maintaining my own full life", value: 'secure' },
      { text: "Freedom within commitment — a partner who respects my independence", value: 'avoidant' },
      { text: "Consistency and patience — someone who doesn't give up when I'm complicated", value: 'disorganized' },
    ],
  },
  // ── CONFLICT & REPAIR (4) ──────────────────────────────────────────────────
  {
    type: 'lifestyle', backendId: 6,
    section: 'Conflict',
    question: "When I'm hurt or angry in a relationship, my most honest tendency:",
    options: [
      { text: "Express it — sometimes with more heat than I intend, but I'd rather fight than stew", value: 'expressive' },
      { text: "Take space first, process internally, come back calmly", value: 'processor' },
      { text: "Go quiet — I need time and sometimes struggle to come back to it at all", value: 'withdrawer' },
      { text: "Talk it through immediately — unresolved tension is very uncomfortable", value: 'resolver' },
    ],
  },
  {
    type: 'lifestyle', backendId: 7,
    section: 'Conflict',
    question: "After a significant argument, repair looks like:",
    options: [
      { text: "A real conversation — I need us to understand each other, not just move on", value: 'verbal' },
      { text: "Acts of care — doing something kind or spending quality time resets things", value: 'action' },
      { text: "Time — I need to decompress fully before I can reconnect", value: 'time' },
      { text: "Physical reconnection — touch heals more than words", value: 'physical' },
    ],
  },
  {
    type: 'lifestyle', backendId: 8,
    section: 'Conflict',
    question: "When my partner is upset with me, even if I think I'm right:",
    options: [
      { text: "I want to understand their experience first — I can hold my view and be genuinely curious", value: 'empathic' },
      { text: "I defend myself — it's hard not to explain my side when I feel accused unfairly", value: 'defensive' },
      { text: "I shut down — high emotional intensity makes it hard to stay present", value: 'stonewalling' },
      { text: "I apologize quickly to reduce tension, even if I'm not sure what I did wrong", value: 'appeasing' },
    ],
  },
  {
    type: 'lifestyle', backendId: 9,
    section: 'Conflict',
    question: "What I most need from a partner during conflict:",
    options: [
      { text: "To be heard and validated — even if we disagree, I need to feel my feelings make sense", value: 'validation' },
      { text: "Space and patience — don't push me when I'm flooded", value: 'space' },
      { text: "Honesty and directness — I'd rather a hard truth than comfortable avoidance", value: 'honesty' },
      { text: "Humor or lightness — the ability to take a breath and not catastrophize", value: 'levity' },
    ],
  },

  // ── LIFE VISION & VALUES (8) ───────────────────────────────────────────────
  {
    type: 'lifestyle', backendId: 10,
    section: 'Values',
    question: "My relationship with spirituality, religion, or a higher power is:",
    options: [
      { text: "Central to my identity — faith or spiritual practice is a daily, meaningful part of how I live", value: 'devout' },
      { text: "Personally meaningful but private — I have a spiritual life but don't need it shared or matched", value: 'personal' },
      { text: "Curious and open — I explore ideas but don't hold fixed beliefs", value: 'open' },
      { text: "Not a part of my worldview — I'm secular and not drawn toward spiritual frameworks", value: 'secular' },
    ],
  },
  {
    type: 'lifestyle', backendId: 11,
    section: 'Values',
    question: "My honest relationship with money and financial life is:",
    options: [
      { text: "Security-focused — I save carefully, dislike debt, financial stability is a core priority", value: 'security' },
      { text: "Experience-focused — I spend on what enriches life and worry less about accumulating", value: 'experience' },
      { text: "Building-focused — I'm actively working toward wealth or financial independence", value: 'building' },
      { text: "Generous-focused — giving and providing for people I love is how I relate to money", value: 'generous' },
    ],
  },
  {
    type: 'lifestyle', backendId: 12,
    section: 'Values',
    question: "When it comes to financial dynamics in a relationship:",
    note: "Completely private — never shown to matches.",
    options: [
      { text: "I want to be provided for — financial security from a partner is important to me", value: 'provided_for' },
      { text: "I want us to be financially equal — shared contribution and independence matter", value: 'equal' },
      { text: "I want to be the provider — taking care of my partner financially is meaningful to me", value: 'provider' },
      { text: "I'm flexible — I care more about values and generosity than the specific dynamic", value: 'flexible' },
    ],
  },
  {
    type: 'lifestyle', backendId: 17,
    section: 'Values',
    question: "Where I want my life to be in 5 years looks most like:",
    options: [
      { text: "Rooted — settled somewhere I love, stable home, community, long-term relationships", value: 'rooted' },
      { text: "Growing — building something significant that demands real investment", value: 'building' },
      { text: "Exploring — more travel, more experiences, more openness about where life might lead", value: 'exploring' },
      { text: "Simplifying — less complexity, more presence, depth over breadth", value: 'simplifying' },
    ],
  },
  {
    type: 'lifestyle', backendId: 18,
    section: 'Values',
    question: "My political and social worldview is something I need a partner to:",
    options: [
      { text: "Share closely — significant differences here would create real friction", value: 'must_align' },
      { text: "Be broadly compatible with — some distance is fine, extremes would be a problem", value: 'broadly_compatible' },
      { text: "Respect without needing to agree — I value diverse perspectives", value: 'tolerant' },
      { text: "Leave out of the relationship — politics and relationships are separate for me", value: 'separate' },
    ],
  },
  {
    type: 'lifestyle', backendId: 23,
    section: 'Values',
    question: "When it comes to falling in love and emotional investment:",
    note: "This is about how you're wired — neither answer is better or worse.",
    options: [
      { text: "I can only fully open my heart when I know it has long term potential — I protect myself until I know it's real", value: 'commitment_first' },
      { text: "I can love fully in the present — the depth of a connection has value regardless of how long it lasts", value: 'present_love' },
    ],
  },

  // ── LIFESTYLE (8) ──────────────────────────────────────────────────────────
  {
    type: 'lifestyle', backendId: 24,
    section: 'Lifestyle',
    question: "My natural social energy is:",
    options: [
      { text: "Outward — I recharge through people, love hosting or going out, and need a social life", value: 'extroverted' },
      { text: "Mixed — I love connection but need real downtime to recover and feel like myself", value: 'ambivert' },
      { text: "Inward — I recharge in quiet and solitude, and need a partner who understands this isn't rejection", value: 'introverted' },
      { text: "Variable — shifts a lot depending on season of life, stress, or energy level", value: 'variable' },
    ],
  },
  {
    type: 'lifestyle', backendId: 26,
    section: 'Lifestyle',
    question: "My relationship with travel in my life:",
    options: [
      { text: "I want to see as much of the world as possible — travel is a core part of how I want to live", value: 'world_explorer' },
      { text: "I love some travel — a few meaningful trips a year feels right", value: 'some_travel' },
      { text: "I'm mostly a homebody — I appreciate an occasional trip but it's not a priority", value: 'homebody' },
      { text: "I prefer to stay close to home — roots and familiarity matter more to me", value: 'rooted' },
    ],
  },
  {
    type: 'lifestyle', backendId: 27,
    section: 'Lifestyle',
    question: "My relationship with health and lifestyle:",
    options: [
      { text: "Health is a core part of how I live — clean eating, movement, and conscious living are daily priorities", value: 'core' },
      { text: "I try and care about it — I make decent choices most of the time", value: 'trying' },
      { text: "I'm pretty relaxed about it — I don't think much about structured health routines", value: 'relaxed' },
      { text: "I just live my life the way I like to", value: 'my_way' },
    ],
  },
  {
    type: 'lifestyle', backendId: 28,
    section: 'Lifestyle',
    question: "My natural schedule and rhythm is:",
    options: [
      { text: "Morning person — I'm most alive early and wind down earlier in the evening", value: 'morning' },
      { text: "Flexible — I adapt easily to different rhythms", value: 'flexible' },
      { text: "Night person — I come alive in the evening and late nights feel natural", value: 'night' },
      { text: "Irregular — my schedule varies too much to have a consistent pattern", value: 'irregular' },
    ],
  },
  {
    type: 'lifestyle', backendId: 29,
    section: 'Lifestyle',
    question: "My relationship with substances:",
    options: [
      { text: "I don't use any substances", value: 'none' },
      { text: "I use for ceremony, plant medicine, or personal healing and growth", value: 'ceremonial' },
      { text: "I use recreationally — socially, occasionally, part of how I enjoy life", value: 'recreational' },
      { text: "I use regularly and it's part of my everyday lifestyle", value: 'regular' },
    ],
  },
  {
    type: 'lifestyle', backendId: 30,
    section: 'Lifestyle',
    question: "My relationship with my living space:",
    options: [
      { text: "My home is a temple — clean, intentional, beautiful, and energetically clear", value: 'temple' },
      { text: "Clean and comfortable — tidy and maintained but a little mess is fine", value: 'clean_comfortable' },
      { text: "Functional and lived in — comfortable but deep cleaning isn't a priority", value: 'functional' },
      { text: "Honestly pretty chaotic — and I'm at peace with that", value: 'chaotic' },
    ],
  },
  {
    type: 'lifestyle', backendId: 31,
    section: 'Lifestyle',
    question: "Pets in my life:",
    options: [
      { text: "Pets are a non-negotiable part of my life — I have them or plan to", value: 'required' },
      { text: "I love animals and would welcome pets but don't need them to be happy", value: 'love_not_required' },
      { text: "I'm open to a partner who has pets even if it's not my thing", value: 'tolerant' },
      { text: "I'm allergic or not interested in pets and need a partner who understands", value: 'no_pets' },
    ],
  },
  {
    type: 'lifestyle', backendId: 32,
    section: 'Lifestyle',
    question: "Where I want to live:",
    options: [
      { text: "City life — in or close to a major city, walkable, alive, and connected to culture", value: 'city' },
      { text: "Suburban — comfortable, spacious, close enough to the city but with room to breathe", value: 'suburban' },
      { text: "Small town or coastal — slower pace, community feel, beauty over convenience", value: 'small_town' },
      { text: "Rural or farm life — land, nature, animals, and distance from the noise", value: 'rural' },
      { text: "Traveling and location independent — I don't want to be rooted in one place", value: 'nomadic' },
      { text: "Open to anything with the right person", value: 'open' },
    ],
  },

  // ── BIO-HARMONICS: GENETICS (7) ────────────────────────────────────────────
  {
    type: 'dosha', backendId: 1,
    section: 'Bio-Harmonics',
    question: "My body has always naturally been:",
    options: [
      { text: "Lean and light — I've always been the person who can't seem to gain weight no matter what I eat", value: 'vata' },
      { text: "Medium and proportionate — I can gain or lose without too much drama", value: 'pitta' },
      { text: "Solid and substantial — I've always carried more weight, gain easily, built to last", value: 'kapha' },
      { text: "A mix — I've been all of these at different times. Lean phases, solid phases, and everything in between depending on life stage", value: 'vpk' },
    ],
  },
  {
    type: 'dosha', backendId: 2,
    section: 'Bio-Harmonics',
    question: "My skin without products tends to be:",
    options: [
      { text: "Dry and sometimes flaky — needs constant moisture, gets tight in cold weather", value: 'vata' },
      { text: "Reactive and sensitive — oily in places, prone to breakouts or redness", value: 'pitta' },
      { text: "Naturally moisturized — thick and smooth, rarely dry, slow to react", value: 'kapha' },
      { text: "It changes with the season and my stress level — I've had dry spells, oily phases, and stretches of balance", value: 'vpk' },
    ],
  },
  {
    type: 'dosha', backendId: 5,
    section: 'Bio-Harmonics',
    question: "My energy through the day:",
    options: [
      { text: "In bursts — waves of creative energy then I crash hard, my stamina is inconsistent", value: 'vata' },
      { text: "Hot and focused — I can go hard for a long time but I burn out if I don't watch it", value: 'pitta' },
      { text: "Slow start, long finish — takes me a while to warm up but once I'm going I outlast everyone", value: 'kapha' },
      { text: "Variable — I can burst through something or sustain for hours depending on what's called for", value: 'vpk' },
    ],
  },
  {
    type: 'dosha', backendId: 6,
    section: 'Bio-Harmonics',
    question: "My sleep has always been:",
    options: [
      { text: "Light and restless — I wake up easily, fall asleep slowly, mind won't stop at night", value: 'vata' },
      { text: "Pretty solid — I fall asleep without much trouble and wake up feeling sharp", value: 'pitta' },
      { text: "Deep and heavy — I could sleep forever, hit snooze multiple times, wake up foggy", value: 'kapha' },
      { text: "It shifts — I can sleep deep or light, it changes with stress level and season", value: 'vpk' },
    ],
  },
  {
    type: 'dosha', backendId: 9,
    section: 'Bio-Harmonics',
    question: "When stressed I:",
    options: [
      { text: "Spiral a little — my mind races, I overthink everything and sometimes freeze up", value: 'vata' },
      { text: "Get intense — I push hard, get frustrated, become a bit of a control freak under pressure", value: 'pitta' },
      { text: "Go quiet and hunker down — I withdraw, get stubborn, reach for comfort until it passes", value: 'kapha' },
      { text: "All three at different times — I can spiral, push hard, or go quiet depending on the situation", value: 'vpk' },
    ],
  },
  {
    type: 'dosha', backendId: 10,
    section: 'Bio-Harmonics',
    question: "My decision making:",
    options: [
      { text: "Changeable — I can see every angle and committing to one feels almost impossible", value: 'vata' },
      { text: "Decisive — I decide fast and stick with it, sometimes too fast", value: 'pitta' },
      { text: "Deliberate — I take my time, hate being rushed, trust slow over quick", value: 'kapha' },
      { text: "It depends — sometimes I decide immediately, sometimes I need time. The situation drives it", value: 'vpk' },
    ],
  },
  {
    type: 'dosha', backendId: 13,
    section: 'Bio-Harmonics',
    question: "The life that feels most like home:",
    options: [
      { text: "Creative, warm, and always changing — I need novelty, movement, art, and the energy of new things", value: 'vata' },
      { text: "Cool, structured, and driven — I thrive with goals, competition, and people who match my intensity", value: 'pitta' },
      { text: "Slow, natural, and rooted — home, family, nature, routine, and a community I can count on", value: 'kapha' },
      { text: "All of it — I need novelty AND roots, creativity AND structure. I adapt to what the season calls for", value: 'vpk' },
    ],
  },

  // ── BIO-HARMONICS: MIND / TRIGUNA (5) ─────────────────────────────────────
  {
    type: 'dosha', backendId: 14,
    section: 'Bio-Harmonics',
    question: "What honestly drives most of my actions:",
    note: "There's no right answer here — this is about your actual pattern, not your aspirational self.",
    options: [
      { text: "Genuinely trying to do good — I get energy from contributing, growing, and being useful", value: 'sattva' },
      { text: "Wanting to win and be seen — I'm motivated by achievement, recognition, and making my mark", value: 'rajas' },
      { text: "Staying comfortable and safe — I protect what I have and resist things that disrupt my peace", value: 'tamas' },
    ],
  },
  {
    type: 'dosha', backendId: 15,
    section: 'Bio-Harmonics',
    question: "In conflict I:",
    options: [
      { text: "Seek to understand — I can hold my view and be genuinely curious about theirs, truth over winning", value: 'sattva' },
      { text: "Argue to win — I get strategic, push hard, find it genuinely difficult to back down", value: 'rajas' },
      { text: "Disappear — I disengage, go silent, hold a quiet grudge, or pretend it didn't happen", value: 'tamas' },
    ],
  },
  {
    type: 'dosha', backendId: 16,
    section: 'Bio-Harmonics',
    question: "My inner life — meditation, reflection, stillness:",
    options: [
      { text: "Consistent valued practice — I actually show up for it, not perfectly but consistently, and it matters", value: 'sattva' },
      { text: "Sporadic and goal-oriented — I do it when I need something from it, like stress relief", value: 'rajas' },
      { text: "Largely absent — I default to distraction, scrolling, staying busy rather than going inward", value: 'tamas' },
    ],
  },
  {
    type: 'dosha', backendId: 17,
    section: 'Bio-Harmonics',
    question: "My honest relationship with food:",
    options: [
      { text: "Pretty conscious — I think about what I eat and how it makes me feel, meals feel like a ritual", value: 'sattva' },
      { text: "Performance and pleasure — I eat for energy, reward myself with food, love the experience", value: 'rajas' },
      { text: "Emotional and habitual — food is comfort, I reach for it when stressed or bored", value: 'tamas' },
    ],
  },
  {
    type: 'dosha', backendId: 18,
    section: 'Bio-Harmonics',
    question: "When I think about my life direction:",
    options: [
      { text: "Clear and at peace — I have a sense of purpose and move toward it without forcing it", value: 'sattva' },
      { text: "Driven but never quite satisfied — I have big ambitions and I'm always pushing, the finish line keeps moving", value: 'rajas' },
      { text: "Foggy or stuck — I'm not sure what I want or I know but can't seem to move toward it", value: 'tamas' },
    ],
  },

  // ── BIO-HARMONICS: EPIGENETICS / GUNAS (5) ────────────────────────────────
  {
    type: 'dosha', backendId: 19,
    section: 'Bio-Harmonics',
    question: "The way I move through life:",
    options: [
      { text: "Heavy and deliberate — I feel things deeply, move at my own pace, there's a gravity to how I show up", value: 'heavy' },
      { text: "Light and quick — I move fast, think on my feet, and don't tend to get weighed down", value: 'light' },
    ],
  },
  {
    type: 'dosha', backendId: 20,
    section: 'Bio-Harmonics',
    question: "My emotional temperature in relationships tends to run:",
    options: [
      { text: "Warm to hot — I feel things intensely, express quickly, bring a lot of heat and passion", value: 'hot' },
      { text: "Cool and steady — I feel deeply but evenly, I rarely flare up, I'd always choose calm over intensity", value: 'cold' },
    ],
  },
  {
    type: 'dosha', backendId: 21,
    section: 'Bio-Harmonics',
    question: "When things change or life gets uncertain:",
    options: [
      { text: "I need ground first — change is genuinely hard for me, I need to feel settled before moving forward", value: 'stable' },
      { text: "I roll with it — I adapt fast, get restless with too much sameness, change energizes me", value: 'mobile' },
    ],
  },
  {
    type: 'dosha', backendId: 22,
    section: 'Bio-Harmonics',
    question: "The way I communicate and think is naturally:",
    options: [
      { text: "Sharp and direct — I get to the point, say exactly what I mean, and appreciate the same from others", value: 'sharp' },
      { text: "Soft and considered — I take my time with words, warmth matters more to me than precision", value: 'soft' },
    ],
  },
  {
    type: 'dosha', backendId: 23,
    section: 'Bio-Harmonics',
    question: "The way I give and receive love:",
    options: [
      { text: "Flowing and open — I move toward people easily, affection comes naturally, I don't resist closeness", value: 'smooth' },
      { text: "Boundaried and self-contained — I'm warm but I need space to be myself within closeness", value: 'dry' },
    ],
  },

];
